import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/utils/shadcn-ui";

/**
 * The checkbox every other one is built on. Given a `label`, it renders the box
 * and that text inside one `<label>`, so clicking the text toggles the box too
 * - the way to put text beside a checkbox, rather than a caller wrapping its
 * own. Inline, so the clickable area ends where the text does rather than
 * running on across the row. A disabled box gives its text the same
 * not-allowed cursor.
 * `labelClassName` styles that wrapper; `className` stays on the box.
 */
function BaseCheckbox({
  className,
  label,
  labelClassName,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root> & {
  label?: React.ReactNode;
  labelClassName?: string;
}) {
  const box = (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      className={cn(
        "peer border-input dark:bg-input/30 data-[state=checked]:bg-brand-primary data-[state=checked]:text-brand-primary-foreground data-[state=checked]:border-brand-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className="grid place-content-center text-current transition-none"
      >
        <CheckIcon className="size-3.5 text-current" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (label == null) return box;

  return (
    <label
      className={cn(
        "inline-flex cursor-pointer items-center gap-2 has-disabled:cursor-not-allowed",
        labelClassName,
      )}
    >
      {box}
      {label}
    </label>
  );
}

export { BaseCheckbox };
